import React, { useEffect, useState } from 'react';
import { getEmpleados, createEmpleado, updateEmpleado, updateEmpleadoEstado, getContratoEmpleadoPdfUrl } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Users, Phone, Download, Edit, MoreVertical, Briefcase } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

const estadoColors = {
    activo: 'bg-accent/10 text-accent border-accent/20',
    inactivo: 'bg-muted text-muted-foreground border-muted',
    vacaciones: 'bg-primary/10 text-primary border-primary/20',
    licencia: 'bg-warning/10 text-warning border-warning/20',
};

const tipoContratoLabels = {
    fijo: 'Indefinido',
    temporal: 'Temporal',
    prueba: 'Prueba',
};

export default function EmpleadosPage() {
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEmpleado, setEditingEmpleado] = useState(null);
    const [form, setForm] = useState({
        cedula: '',
        nombre: '',
        cargo: '',
        departamento: '',
        fecha_ingreso: new Date().toISOString().split('T')[0],
        salario_base: '',
        telefono: '',
        direccion: '',
        email: '',
        cuenta_banco: '',
        banco: '',
        tipo_contrato: 'fijo',
    });

    useEffect(() => {
        loadEmpleados();
    }, []);

    const loadEmpleados = async () => {
        try {
            const { data } = await getEmpleados();
            setEmpleados(data);
        } catch (error) {
            toast.error('Error al cargar empleados');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...form,
                salario_base: parseFloat(form.salario_base),
            };
            if (editingEmpleado) {
                await updateEmpleado(editingEmpleado.id, data);
                toast.success('Empleado actualizado');
            } else {
                await createEmpleado(data);
                toast.success('Empleado creado');
            }
            setDialogOpen(false);
            resetForm();
            loadEmpleados();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar empleado');
        }
    };

    const handleEdit = (empleado) => {
        setEditingEmpleado(empleado);
        setForm({
            cedula: empleado.cedula,
            nombre: empleado.nombre,
            cargo: empleado.cargo,
            departamento: empleado.departamento,
            fecha_ingreso: empleado.fecha_ingreso,
            salario_base: empleado.salario_base.toString(),
            telefono: empleado.telefono,
            direccion: empleado.direccion,
            email: empleado.email || '',
            cuenta_banco: empleado.cuenta_banco || '',
            banco: empleado.banco || '',
            tipo_contrato: empleado.tipo_contrato,
        });
        setDialogOpen(true);
    };

    const handleEstado = async (empleadoId, estado) => {
        try {
            await updateEmpleadoEstado(empleadoId, estado);
            toast.success(`Estado actualizado`);
            loadEmpleados();
        } catch (error) {
            toast.error('Error al actualizar estado');
        }
    };

    const downloadContrato = (empleadoId) => {
        const token = localStorage.getItem('token');
        const url = getContratoEmpleadoPdfUrl(empleadoId);
        window.open(`${url}?token=${token}`, '_blank');
    };

    const resetForm = () => {
        setEditingEmpleado(null);
        setForm({
            cedula: '',
            nombre: '',
            cargo: '',
            departamento: '',
            fecha_ingreso: new Date().toISOString().split('T')[0],
            salario_base: '',
            telefono: '',
            direccion: '',
            email: '',
            cuenta_banco: '',
            banco: '',
            tipo_contrato: 'fijo',
        });
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    const totalNomina = empleados.filter(e => e.estado === 'activo').reduce((sum, e) => sum + e.salario_base, 0);

    return (
        <div className="space-y-6" data-testid="empleados-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Empleados</h1>
                    <p className="text-muted-foreground mt-1">
                        {empleados.length} empleados • Nómina mensual: {formatCurrency(totalNomina)}
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button onClick={resetForm} data-testid="new-empleado-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nuevo Empleado
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingEmpleado ? 'Editar Empleado' : 'Nuevo Empleado'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Cédula</Label>
                                    <Input
                                        value={form.cedula}
                                        onChange={(e) => setForm({ ...form, cedula: e.target.value })}
                                        required
                                        disabled={!!editingEmpleado}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Teléfono</Label>
                                    <Input
                                        value={form.telefono}
                                        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Nombre Completo</Label>
                                <Input
                                    value={form.nombre}
                                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Cargo</Label>
                                    <Input
                                        value={form.cargo}
                                        onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                                        required
                                        placeholder="Ej: Cobrador, Cajero"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Departamento</Label>
                                    <Input
                                        value={form.departamento}
                                        onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                                        required
                                        placeholder="Ej: Cobranza, Administración"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Salario Base</Label>
                                    <Input
                                        type="number"
                                        value={form.salario_base}
                                        onChange={(e) => setForm({ ...form, salario_base: e.target.value })}
                                        required
                                        min="0"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Tipo de Contrato</Label>
                                    <Select
                                        value={form.tipo_contrato}
                                        onValueChange={(value) => setForm({ ...form, tipo_contrato: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="fijo">Indefinido</SelectItem>
                                            <SelectItem value="temporal">Temporal</SelectItem>
                                            <SelectItem value="prueba">Prueba (90 días)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Fecha de Ingreso</Label>
                                    <Input
                                        type="date"
                                        value={form.fecha_ingreso}
                                        onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Dirección</Label>
                                <Input
                                    value={form.direccion}
                                    onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Banco</Label>
                                    <Input
                                        value={form.banco}
                                        onChange={(e) => setForm({ ...form, banco: e.target.value })}
                                        placeholder="Ej: Popular, BHD"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Cuenta Bancaria</Label>
                                    <Input
                                        value={form.cuenta_banco}
                                        onChange={(e) => setForm({ ...form, cuenta_banco: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button type="submit" data-testid="empleado-submit-btn">
                                    {editingEmpleado ? 'Actualizar' : 'Crear'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Empleado</TableHead>
                                    <TableHead>Cargo</TableHead>
                                    <TableHead className="hidden md:table-cell">Salario</TableHead>
                                    <TableHead className="hidden lg:table-cell">Ingreso</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="w-20"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {empleados.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-12">
                                            <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">No hay empleados registrados</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    empleados.map((empleado) => (
                                        <TableRow key={empleado.id} className="animate-fadeIn">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{empleado.nombre}</p>
                                                    <p className="text-sm text-muted-foreground font-mono">{empleado.cedula}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{empleado.cargo}</p>
                                                    <p className="text-xs text-muted-foreground">{empleado.departamento}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell font-mono">
                                                {formatCurrency(empleado.salario_base)}
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell">
                                                {empleado.fecha_ingreso}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={estadoColors[empleado.estado]}>
                                                    {empleado.estado}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleEdit(empleado)}>
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Editar
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => downloadContrato(empleado.id)}>
                                                            <Download className="w-4 h-4 mr-2" />
                                                            Contrato PDF
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleEstado(empleado.id, 'activo')}>
                                                            Marcar Activo
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleEstado(empleado.id, 'vacaciones')}>
                                                            En Vacaciones
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleEstado(empleado.id, 'inactivo')}>
                                                            Desactivar
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
